import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { X, Plus, Sparkles, CalendarIcon } from 'lucide-react';
import {
  AppRuleTrigger,
  TriggerType,
  getTriggerTypeLabel,
} from '@polybank/shared';
import { useCallback } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { RuleFormValues } from '@/components/rules/form/rule-form-types';
import dayjs from 'dayjs';

interface RuleTriggerBuilderProps {
  isMobile?: boolean;
}

export function RuleTriggerBuilder({
  isMobile = false,
}: RuleTriggerBuilderProps) {
  const { control } = useFormContext<RuleFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'triggers',
  });
  const addTrigger = useCallback(() => {
    append({
      triggerType: TriggerType.DESCRIPTION_CONTAINS,
      triggerValue: '',
    } as AppRuleTrigger);
  }, [append]);

  const removeTrigger = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  const getInputPlaceholder = useCallback((type: string) => {
    if (type.startsWith('amount_')) {
      if (type.includes('between')) return 'e.g., 100,500';
      return 'e.g., 100.00';
    }
    if (type.startsWith('DESCRIPTION_')) {
      return 'e.g., Starbucks';
    }
    return '';
  }, []);

  const isDateTrigger = useCallback((type: string) => {
    return (
      type === TriggerType.DATE_STARTING_FROM ||
      type === TriggerType.DATE_UNTIL ||
      type === TriggerType.DATE_BETWEEN
    );
  }, []);

  const isAmountTrigger = useCallback((type: string) => {
    return type.startsWith('amount_');
  }, []);

  const renderValueInput = useCallback(
    (
      triggerType: string,
      controlField: { value: string; onChange: (value: string) => void },
      height: string
    ) => {
      if (triggerType.startsWith('DESCRIPTION_')) {
        return (
          <Textarea
            value={controlField.value}
            onChange={(e) => controlField.onChange(e.target.value)}
            placeholder={getInputPlaceholder(triggerType)}
            rows={2}
            className='resize-none'
          />
        );
      }
      if (isDateTrigger(triggerType)) {
        if (triggerType === 'date_between') {
          const [fromStr, toStr] = (controlField.value || ',').split(',');
          const fromDate = fromStr ? dayjs(fromStr) : null;
          const toDate = toStr ? dayjs(toStr) : null;
          const updateBetween = (from: string, to: string) => {
            controlField.onChange(`${from},${to}`);
          };
          return (
            <div className='flex gap-2 items-center'>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    className={`${height} flex-1 justify-start text-left font-normal ${!fromDate?.isValid() ? 'text-muted-foreground' : ''}`}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {fromDate?.isValid()
                      ? fromDate.format('MMM D, YYYY')
                      : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' usePortal={false}>
                  <Calendar
                    mode='single'
                    selected={
                      fromDate?.isValid() ? fromDate.toDate() : undefined
                    }
                    onSelect={(date) => {
                      updateBetween(
                        date ? dayjs(date).format('YYYY-MM-DD') : '',
                        toStr || ''
                      );
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <span className='text-muted-foreground text-sm'>to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type='button'
                    variant='outline'
                    className={`${height} flex-1 justify-start text-left font-normal ${!toDate?.isValid() ? 'text-muted-foreground' : ''}`}
                  >
                    <CalendarIcon className='mr-2 h-4 w-4' />
                    {toDate?.isValid() ? toDate.format('MMM D, YYYY') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' usePortal={false}>
                  <Calendar
                    mode='single'
                    selected={toDate?.isValid() ? toDate.toDate() : undefined}
                    onSelect={(date) => {
                      updateBetween(
                        fromStr || '',
                        date ? dayjs(date).format('YYYY-MM-DD') : ''
                      );
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          );
        }
        const dateValue = controlField.value ? dayjs(controlField.value) : null;
        return (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type='button'
                variant='outline'
                className={`${height} w-full justify-start text-left font-normal ${!dateValue ? 'text-muted-foreground' : ''}`}
              >
                <CalendarIcon className='mr-2 h-4 w-4' />
                {dateValue?.isValid()
                  ? dateValue.format('MMM D, YYYY')
                  : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' usePortal={false}>
              <Calendar
                mode='single'
                selected={dateValue?.isValid() ? dateValue.toDate() : undefined}
                onSelect={(date) => {
                  controlField.onChange(
                    date ? dayjs(date).format('YYYY-MM-DD') : ''
                  );
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        );
      }
      return (
        <Input
          value={controlField.value}
          onChange={(e) => controlField.onChange(e.target.value)}
          type={isAmountTrigger(triggerType) ? 'number' : 'text'}
          inputMode={isAmountTrigger(triggerType) ? 'decimal' : undefined}
          step={isAmountTrigger(triggerType) ? 'any' : undefined}
          placeholder={getInputPlaceholder(triggerType)}
          className={height}
        />
      );
    },
    [getInputPlaceholder, isDateTrigger, isAmountTrigger]
  );

  // Mobile layout
  if (isMobile) {
    return (
      <div className='space-y-4'>
        {fields.length === 0 ? (
          <Card
            role='button'
            tabIndex={0}
            className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
            onClick={addTrigger}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                addTrigger();
              }
            }}
          >
            <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
              <div className='w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-blue-500/10 to-purple-600/10 flex items-center justify-center'>
                <Sparkles className='w-6 h-6 text-blue-600' />
              </div>
              <p className='font-medium text-slate-900 mb-1'>
                Add your first trigger
              </p>
              <p className='text-sm text-slate-500'>
                Define when this rule should apply to transactions
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {fields.map((field, index) => (
              <Card key={field.id} className='relative'>
                <CardContent className='p-4'>
                  <div className='flex items-start justify-between gap-3'>
                    <div className='flex-1 space-y-3'>
                      <div className='flex items-center gap-2'>
                        {index === 0 && (
                          <div className='text-sm font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded'>
                            When
                          </div>
                        )}
                        {index > 0 && (
                          <div className='text-sm font-medium text-purple-600 bg-purple-50 px-2 py-1 rounded'>
                            AND
                          </div>
                        )}
                      </div>

                      <div className='grid grid-cols-1 gap-3'>
                        <div className='space-y-2'>
                          <Label className='text-sm font-medium text-slate-700'>
                            Type
                          </Label>
                          <Controller
                            name={`triggers.${index}.triggerType`}
                            control={control}
                            render={({ field: controlField }) => (
                              <Select
                                value={controlField.value}
                                onValueChange={controlField.onChange}
                              >
                                <SelectTrigger className='h-10'>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {Object.values(TriggerType).map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {getTriggerTypeLabel(type)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        <div className='space-y-2'>
                          <Label className='text-sm font-medium text-slate-700'>
                            Value
                          </Label>
                          <Controller
                            name={`triggers.${index}.triggerValue`}
                            control={control}
                            render={({ field: controlField }) =>
                              renderValueInput(
                                field.triggerType,
                                controlField,
                                'h-10'
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      onClick={() => removeTrigger(index)}
                      className='text-slate-400 hover:text-red-500 hover:bg-red-50 mt-1'
                    >
                      <X className='w-4 h-4' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Empty card for adding new trigger */}
            <Card
              role='button'
              tabIndex={0}
              className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
              onClick={addTrigger}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  addTrigger();
                }
              }}
            >
              <CardContent className='flex flex-col items-center justify-center py-6 text-center'>
                <div className='w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-slate-500/10 to-slate-700/10 flex items-center justify-center'>
                  <Plus className='w-5 h-5 text-slate-600' />
                </div>
                <p className='font-medium text-slate-900 text-sm'>
                  Add another trigger
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Desktop layout (improved)
  return (
    <div className='space-y-6'>
      {fields.length === 0 ? (
        <Card
          className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
          onClick={addTrigger}
        >
          <CardContent className='flex flex-col items-center justify-center py-12 text-center'>
            <div className='w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500/10 to-indigo-600/10 flex items-center justify-center'>
              <Sparkles className='w-8 h-8 text-blue-600' />
            </div>
            <p className='font-medium text-slate-900 mb-2 text-lg'>
              Add your first trigger
            </p>
            <p className='text-sm text-slate-500 max-w-sm'>
              Define when this rule should apply to transactions
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {fields.map((field, index) => (
            <Card key={field.id} className='relative'>
              <CardContent className='p-6'>
                <div className='flex items-start justify-between gap-4'>
                  <div className='flex-1 space-y-4'>
                    <div className='flex items-center gap-3'>
                      <div className='flex-1 items-center justify-items-start'>
                        {index === 0 && (
                          <div className=' text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full'>
                            When
                          </div>
                        )}
                        {index > 0 && (
                          <div className='text-sm font-medium text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-full'>
                            AND
                          </div>
                        )}
                      </div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        onClick={() => removeTrigger(index)}
                        className='text-slate-400 hover:text-red-500 hover:bg-red-50 mt-2'
                      >
                        <X className='w-5 h-5' />
                      </Button>
                    </div>

                    <div className='grid grid-cols-1  gap-4'>
                      <div className='space-y-2'>
                        <Label className='text-sm font-medium text-slate-700'>
                          Type
                        </Label>
                        <Controller
                          name={`triggers.${index}.triggerType`}
                          control={control}
                          render={({ field: controlField }) => (
                            <Select
                              value={controlField.value}
                              onValueChange={controlField.onChange}
                            >
                              <SelectTrigger className='h-11'>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.values(TriggerType).map((type) => (
                                  <SelectItem key={type} value={type}>
                                    {getTriggerTypeLabel(type)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>

                      <div className='space-y-2 lg:col-span-2'>
                        <Label className='text-sm font-medium text-slate-700'>
                          Value
                        </Label>
                        <Controller
                          name={`triggers.${index}.triggerValue`}
                          control={control}
                          render={({ field: controlField }) =>
                            renderValueInput(
                              field.triggerType,
                              controlField,
                              'h-11'
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Empty card for adding new trigger */}
          <Card
            role='button'
            tabIndex={0}
            className='border-dashed border-2 hover:bg-slate-50/50 transition-colors cursor-pointer'
            onClick={addTrigger}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                addTrigger();
              }
            }}
          >
            <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
              <div className='w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-slate-500/10 to-slate-700/10 flex items-center justify-center'>
                <Plus className='w-6 h-6 text-slate-600' />
              </div>
              <p className='font-medium text-slate-900 text-base'>
                {fields.length > 0 ? 'Add another trigger' : 'Add a trigger'}
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
